'use client';
import React, { useState } from 'react';
import { CheckCircleIcon, CalendarDaysIcon, StarIcon, ClockIcon, UserGroupIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

import { createClient } from '@/lib/supabase/client';

interface FormData {
  fullName: string;
  company: string;
  email: string;
  phone: string;
  companySize: string;
  modules: string[];
  preferredDate: string;
  preferredTime: string;
  message: string;
  selectedProduct: string;
}

interface BookingRecord {
  id: string;
  fullName: string;
  company: string;
  email: string;
  preferredDate: string;
  preferredTime: string;
  modules: string[];
  selectedProduct: string;
  bookingStatus: string;
}

const moduleOptions = ['Finance & Accounting', 'HR & Payroll', 'Inventory Management', 'CRM & Sales', 'Supply Chain', 'Manufacturing'];
const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];
const companySizes = ['1–50 employees', '51–200 employees', '201–1,000 employees', '1,000+ employees'];
const productOptions = [
  'MANG ASSET', 'ENYANDENI CLOCKING', 'PROP FLOW', 'CemeteryMS',
  'Gate Register', 'Municipal Customer Care', 'EDMS', 'Store Management', 'General / Not Sure'
];

const expectations = [
  { icon: ClockIcon, title: '45-min personalized demo', desc: 'Tailored to your industry and the modules you care about most.' },
  { icon: UserGroupIcon, title: 'Live Q&A with an ERP specialist', desc: 'Ask anything — implementation, pricing, migration, integrations.' },
  { icon: ChartBarIcon, title: 'Custom pricing quote', desc: 'Walk away with a clear, itemized quote for your exact requirements.' },
  { icon: CalendarDaysIcon, title: 'Implementation roadmap', desc: 'A realistic timeline and milestone plan for going live.' },
];

export default function BookDemoPage() {
  const [form, setForm] = useState<FormData>({
    fullName: '', company: '', email: '', phone: '',
    companySize: '', modules: [], preferredDate: '', preferredTime: '',
    message: '', selectedProduct: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [booking, setBooking] = useState<BookingRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const toggleModule = (mod: string) => {
    setForm((prev) => ({
      ...prev,
      modules: prev.modules.includes(mod)
        ? prev.modules.filter((m) => m !== mod)
        : [...prev.modules, mod],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError(null);

    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('demo_bookings')
        .insert({
          full_name: form.fullName,
          company: form.company,
          email: form.email,
          phone: form.phone || null,
          company_size: form.companySize || null,
          modules: form.modules,
          preferred_date: form.preferredDate,
          preferred_time: form.preferredTime,
          message: form.message || null,
          selected_product: form.selectedProduct || null,
          booking_status: 'pending',
        })
        .select('id, full_name, company, email, preferred_date, preferred_time, modules, selected_product, booking_status')
        .single();

      if (error) {
        console.error('Booking error:', error.message);
        setSubmitError('Failed to save your booking. Please try again.');
        return;
      }

      setBooking({
        id: data.id,
        fullName: data.full_name,
        company: data.company,
        email: data.email,
        preferredDate: data.preferred_date,
        preferredTime: data.preferred_time,
        modules: data.modules || [],
        selectedProduct: data.selected_product || '',
        bookingStatus: data.booking_status,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      console.error('Unexpected error:', err);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1B2D]">
      <Navbar />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

          {/* Form — 3/5 */}
          <div className="lg:col-span-3">
            <p className="section-label mb-2">Free Demo</p>
            <h1 className="text-3xl font-extrabold text-white mb-2">Book Your Free MANGQASHA IT SOLUTIONS Demo</h1>
            <p className="text-[#94A3B8] text-sm mb-8">No commitment. No sales pressure. Just a clear look at what MANGQASHA IT SOLUTIONS can do for your business.</p>

            {submitted && booking ? (
              <div className="bg-[#162236] border border-[#00C9A7]/30 rounded-2xl p-10 text-center teal-glow fade-in">
                <div className="w-16 h-16 rounded-full bg-[#00C9A7]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-8 h-8 text-[#00C9A7]" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Demo Scheduled!</h2>
                <p className="text-[#94A3B8] text-sm mb-1">
                  Thanks, <span className="text-white font-semibold">{booking.fullName}</span>. We've received your request.
                </p>
                <p className="text-[#94A3B8] text-sm">
                  A confirmation will be sent to <span className="text-white">{booking.email}</span>. Our team will reach out within 1 business day to confirm your slot.
                </p>
                <div className="mt-6 bg-[#0F1B2D] border border-[#1E3352] rounded-xl p-4 text-left">
                  <p className="text-xs text-[#64748B] mb-2">Your booking summary</p>
                  <div className="space-y-1">
                    <p className="text-sm text-white"><span className="text-[#64748B]">Booking ID:</span> <span className="font-mono text-xs text-[#00C9A7]">{booking.id}</span></p>
                    <p className="text-sm text-white"><span className="text-[#64748B]">Status:</span> <span className="capitalize text-[#F59E0B]">{booking.bookingStatus}</span></p>
                    <p className="text-sm text-white"><span className="text-[#64748B]">Company:</span> {booking.company}</p>
                    <p className="text-sm text-white"><span className="text-[#64748B]">Date:</span> {booking.preferredDate}</p>
                    <p className="text-sm text-white"><span className="text-[#64748B]">Time:</span> {booking.preferredTime}</p>
                    {booking.selectedProduct && (
                      <p className="text-sm text-white"><span className="text-[#64748B]">Product:</span> {booking.selectedProduct}</p>
                    )}
                    <p className="text-sm text-white"><span className="text-[#64748B]">Modules:</span> {booking.modules?.length > 0 ? booking.modules.join(', ') : 'General overview'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {submitError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-400">
                    {submitError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Full Name *</label>
                    <input
                      required
                      type="text"
                      value={form.fullName}
                      onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                      placeholder="Jane Smith"
                      className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#3D5068] focus:outline-none focus:border-[#00C9A7] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Company Name *</label>
                    <input
                      required
                      type="text"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      placeholder="Acme Corp"
                      className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#3D5068] focus:outline-none focus:border-[#00C9A7] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Work Email *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@acmecorp.com"
                      className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#3D5068] focus:outline-none focus:border-[#00C9A7] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                      className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#3D5068] focus:outline-none focus:border-[#00C9A7] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Company Size *</label>
                  <select
                    required
                    value={form.companySize}
                    onChange={(e) => setForm({ ...form, companySize: e.target.value })}
                    className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C9A7] transition-colors"
                  >
                    <option value="" className="bg-[#162236]">Select company size</option>
                    {companySizes.map((s) => <option key={s} value={s} className="bg-[#162236]">{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Product of Interest</label>
                  <select
                    value={form.selectedProduct}
                    onChange={(e) => setForm({ ...form, selectedProduct: e.target.value })}
                    className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C9A7] transition-colors"
                  >
                    <option value="" className="bg-[#162236]">Select a product (optional)</option>
                    {productOptions.map((p) => <option key={p} value={p} className="bg-[#162236]">{p}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#94A3B8] mb-2">ERP Modules of Interest</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {moduleOptions.map((mod) => (
                      <button
                        key={mod}
                        type="button"
                        onClick={() => toggleModule(mod)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors text-left ${
                          form.modules.includes(mod)
                            ? 'bg-[#00C9A7]/10 border-[#00C9A7]/40 text-[#00C9A7]'
                            : 'bg-[#162236] border-[#1E3352] text-[#94A3B8] hover:border-[#00C9A7]/30'
                        }`}
                      >
                        {mod}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Preferred Date *</label>
                    <input
                      required
                      type="date"
                      value={form.preferredDate}
                      onChange={(e) => setForm({ ...form, preferredDate: e.target.value })}
                      className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C9A7] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Preferred Time *</label>
                    <select
                      required
                      value={form.preferredTime}
                      onChange={(e) => setForm({ ...form, preferredTime: e.target.value })}
                      className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C9A7] transition-colors"
                    >
                      <option value="" className="bg-[#162236]">Select time slot</option>
                      {timeSlots.map((t) => <option key={t} value={t} className="bg-[#162236]">{t}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5">Anything else we should know?</label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your current systems, pain points, or specific questions..."
                    className="w-full bg-[#162236] border border-[#1E3352] rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#3D5068] focus:outline-none focus:border-[#00C9A7] transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving your booking...
                    </>
                  ) : (
                    <>
                      <CalendarDaysIcon className="w-5 h-5" /> Schedule My Demo
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info — 2/5 */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-[#162236] border border-[#1E3352] rounded-2xl p-6">
              <h3 className="text-base font-bold text-white mb-4">What to Expect</h3>
              <div className="space-y-4">
                {expectations.map((e) => {
                  const ExpIcon = e.icon;
                  return (
                    <div key={e.title} className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#00C9A7]/10 flex items-center justify-center shrink-0">
                        <ExpIcon className="w-4 h-4 text-[#00C9A7]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{e.title}</p>
                        <p className="text-xs text-[#64748B] mt-0.5">{e.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-[#162236] border border-[#1E3352] rounded-2xl p-6">
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-[#00C9A7]">500+</p>
                  <p className="text-xs text-[#64748B]">Demos completed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-[#00C9A7]">4.9/5</p>
                  <p className="text-xs text-[#64748B]">Average rating</p>
                </div>
              </div>
              <div className="border-t border-[#1E3352] pt-4">
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="w-3.5 h-3.5 text-[#F59E0B] fill-[#F59E0B]" />
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8] italic leading-relaxed">
                  "The demo was incredibly thorough. They showed us exactly how MANGQASHA IT SOLUTIONS would handle our specific workflows. We signed within a week."
                </p>
                <p className="text-xs text-[#64748B] mt-2">— Marcus T., VP Operations, Helix Manufacturing</p>
              </div>
            </div>

            <div className="bg-[#00C9A7]/5 border border-[#00C9A7]/20 rounded-2xl p-5">
              <p className="text-xs font-bold text-[#00C9A7] mb-1">No commitment required</p>
              <p className="text-xs text-[#94A3B8]">Attending a demo doesn't obligate you to purchase. We're here to help you make the right decision for your business.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
